'use client'

import { useMemo } from 'react'

import { cn } from '@mntn-dev/ui-utilities'

import type { ResponsiveOverrides } from '../../hooks/use-responsive.ts'
import { useResponsive } from '../../hooks/use-responsive.ts'
import { TagBase, type TagBaseProps } from './tag-base.tsx'

export type ResponsiveTagListItem = {
  key: string
  value: string
  props?: Omit<TagBaseProps, 'children'>
}

type ResponsiveTagListProps = Readonly<{
  items: ResponsiveTagListItem[]
  className?: string
  responsive?: {
    base: number
    overrides: ResponsiveOverrides<number>
  }
  moreTagProps?: Omit<TagBaseProps, 'children'>
}>

/**
 * Displays a list of tags and optionally truncates the list to the defined number of tags.
 * Truncation can be overridden for different breakpoints.
 */
export const ResponsiveTagList = ({
  items,
  className,
  responsive,
  moreTagProps,
}: ResponsiveTagListProps) => {
  const resolveResponsive = useResponsive()

  const maxVisibleItems = useMemo(() => {
    if (!responsive) {
      return items.length
    }

    return Math.max(
      0,
      Math.floor(resolveResponsive(responsive.base, responsive.overrides))
    )
  }, [items.length, resolveResponsive, responsive])

  const visibleItems = items.slice(0, maxVisibleItems)
  const remainingItemsCount = Math.max(items.length - visibleItems.length, 0)

  return (
    <div className={cn('flex flex-row gap-4 w-auto flex-wrap', className)}>
      {visibleItems.map((item) => (
        <TagBase key={item.key} {...item.props}>
          {item.value}
        </TagBase>
      ))}
      {remainingItemsCount > 0 && (
        <TagBase {...moreTagProps}>{`+${remainingItemsCount} More`}</TagBase>
      )}
    </div>
  )
}

export type { ResponsiveTagListProps }
